import React, { useContext, useEffect, useState, useRef, useMemo, useCallback } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { ApiConfig } from "../../api/apiConfig/apiConfig";
import { ProfileContext } from "../../context/ProfileProvider";
import AuthService from "../../api/services/AuthService";
import { BUY_CRYPTO_SUBMENU_ITEMS } from "../headerBuyCryptoSubmenu";
import { TRADE_SUBMENU_ITEMS } from "../headerTradeSubmenu";
import { FUTURES_SUBMENU_ITEMS } from "../headerFuturesSubmenu";
import { EARNING_SUBMENU_ITEMS } from "../headerEarningSubmenu";
import { MORE_SUBMENU_ITEMS } from "../headerMoreSubmenu";
import { OTC_FORM_PATH, OTC_NAV_LABEL, isOtcNavActive } from "../headerInstitutionalSubmenu";
import { ABOUT_SUBMENU_ITEMS } from "../headerAboutSubmenu";
import { isMoreMenuPlaceholderDestination, isNavPathActive, isAnySubmenuActive } from "../headerNavActive";

const UserHeader = () => {
  const { handleTheme, setThemeUpdated, newStoredTheme } = useContext(ProfileContext);
  const [searchPair, setSearchPair] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [allPairs, setAllPairs] = useState([]);
  const [pairsLoading, setPairsLoading] = useState(false);
  const [pairsFetched, setPairsFetched] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const dropdownRef = useRef(null);

  // Check if current page is an auth page (no nav should be active)
  const isAuthPage = ['/login', '/signup', '/forgot_password'].some(
    path => location.pathname.startsWith(path)
  );

  // Segment-safe active check (avoid `includes()` false positives and shared `/coming_soon` collisions)
  const isActive = (path, exact = true) => {
    if (isAuthPage) return false;
    return isNavPathActive(location.pathname, path, exact);
  };

  const isMoreMenuActiveRoute = MORE_SUBMENU_ITEMS.some(
    (item) => !isMoreMenuPlaceholderDestination(item.to) && isActive(item.to, false),
  );
  const isOtcExecutionNavActive = isOtcNavActive(location.pathname);

  const isAboutMenuActiveRoute = isAnySubmenuActive(location.pathname, ABOUT_SUBMENU_ITEMS) ||
    isActive("/about_us", false);

  const isFuturesNavActive =
    isAnySubmenuActive(location.pathname, FUTURES_SUBMENU_ITEMS) ||
    (location.pathname === "/coming_soon" && new URLSearchParams(location.search).get("from") === "futures");

  // Fetch pairs from API when modal opens
  const fetchPairs = useCallback(async () => {
    if (pairsFetched || pairsLoading) return;
    setPairsLoading(true);
    try {
      const result = await AuthService.getPairs();
      if (result?.success) {
        setAllPairs(result.data || []);
        setPairsFetched(true);
      }
    } catch (error) {
    } finally {
      setPairsLoading(false);
    }
  }, [pairsFetched, pairsLoading]);

  const filteredPairs = useMemo(() => {
    if (!searchPair) return allPairs;
    return allPairs.filter((item) =>
      item?.base_currency?.toLowerCase()?.includes(searchPair?.toLowerCase())
    );
  }, [searchPair, allPairs]);
  const dropdownCloseTimerRef = useRef(null);
  const isPinnedByClickRef = useRef(null);
  const lastHoverOpenTimeRef = useRef(0);
  const headerNavScrollRef = useRef(null);
  const [headerNavScrollUi, setHeaderNavScrollUi] = useState({ enabled: false, canLeft: false, canRight: false });

  // Outside click closes dropdown and mobile nav
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        isPinnedByClickRef.current = null;
        setOpenDropdown(null);
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, []);

  const toggleNavbar = () => setIsOpen(!isOpen);

  const closeNavbar = () => {
    isPinnedByClickRef.current = null;
    if (dropdownCloseTimerRef.current) {
      clearTimeout(dropdownCloseTimerRef.current);
      dropdownCloseTimerRef.current = null;
    }
    setIsOpen(false);
    setOpenDropdown(null);
  };

  const toggleDropdown = (key) => {
    if (dropdownCloseTimerRef.current) {
      clearTimeout(dropdownCloseTimerRef.current);
      dropdownCloseTimerRef.current = null;
    }
    if (window.innerWidth < 1200) {
      setOpenDropdown((prev) => (prev === key ? null : key));
      return;
    }
    const timeSinceHover = Date.now() - (lastHoverOpenTimeRef.current || 0);
    if (openDropdown === key && (isPinnedByClickRef.current === key || timeSinceHover > 350)) {
      isPinnedByClickRef.current = null;
      setOpenDropdown(null);
    } else {
      isPinnedByClickRef.current = key;
      setOpenDropdown(key);
    }
  };

  const openDropdownHover = (key) => {
    if (dropdownCloseTimerRef.current) {
      clearTimeout(dropdownCloseTimerRef.current);
      dropdownCloseTimerRef.current = null;
    }
    if (window.innerWidth >= 1200) {
      lastHoverOpenTimeRef.current = Date.now();
      setOpenDropdown(key);
    }
  };

  const closeDropdownHover = () => {
    if (window.innerWidth < 1200) return;
    if (isPinnedByClickRef.current) return;
    if (dropdownCloseTimerRef.current) clearTimeout(dropdownCloseTimerRef.current);
    dropdownCloseTimerRef.current = setTimeout(() => {
      isPinnedByClickRef.current = null;
      setOpenDropdown(null);
    }, 350);
  };

  useEffect(() => {
    return () => {
      if (dropdownCloseTimerRef.current) clearTimeout(dropdownCloseTimerRef.current);
    };
  }, []);

  useEffect(() => {
    const el = headerNavScrollRef.current;
    if (!el) return;

    const mq = window.matchMedia("(min-width: 1200px) and (max-width: 1367px)");

    const recompute = () => {
      const enabled = mq.matches;
      if (!enabled) {
        setHeaderNavScrollUi({ enabled: false, canLeft: false, canRight: false });
        return;
      }
      const maxScrollLeft = Math.max(0, el.scrollWidth - el.clientWidth);
      const canLeft = el.scrollLeft > 1;
      const canRight = el.scrollLeft < maxScrollLeft - 1;
      setHeaderNavScrollUi({ enabled: true, canLeft, canRight });
    };

    const onScroll = () => recompute();
    const onResize = () => recompute();

    recompute();
    el.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onResize);
    mq.addEventListener?.("change", recompute);

    return () => {
      el.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onResize);
      mq.removeEventListener?.("change", recompute);
    };
  }, []);

  useEffect(() => {
    isPinnedByClickRef.current = null;
    setIsOpen(false);
    setOpenDropdown(null);
  }, [location.pathname]);

  const nextPage = (data) => {
    localStorage.setItem("RecentPair", JSON.stringify(data));
    navigate(`/trade/${data?.base_currency}_${data?.quote_currency}`);
    window.location.reload();
  };

  const loginPage = () => navigate(`/login`);
  const signupPage = () => navigate(`/signup`);

  const handleNavClick = (e, hashId) => {
    e.preventDefault();
    closeNavbar();
    navigate(`/#${hashId}`);
    // Scroll after a tick so the section is in the DOM
    setTimeout(() => {
      const el = document.getElementById(hashId);
      if (el) {
        const headerHeight = document.querySelector("header")?.offsetHeight || 80;
        const elementPosition = el.getBoundingClientRect().top + window.scrollY;
        window.scrollTo({ top: elementPosition - headerHeight, behavior: "smooth" });
      }
    }, 50);
  };

  return (
    <header className="sticky-top">
      <div className="container-fluid">
        <div className="row">
          <div className="col-lg-2 logo_s">
            <div className="logo">
              <Link to="/">
                <img className='mobile_logo' src="/images/argb_mobile-logo.svg" alt="logo" />
                <img className='darkogo' src="/images/logo_light.svg" alt="logo" />
              </Link>
            </div>
          </div>

          <div className="col-lg-6 navigation_s">
            <div className="navigation" ref={dropdownRef}>
              <nav className="navbar navbar-expand-lg">
                <button className="navbar-toggler" type="button" onClick={toggleNavbar}>
                  <span className="navbar-toggler-icon">
                    <img src="/images/toggle_icon.svg" alt="toggle" />
                  </span>
                </button>

                <div className={`collapse navbar-collapse ${isOpen ? "show" : ""}`} id="mainNavbar">
                  <button
                    type="button"
                    className="main-navbar__close d-xl-none"
                    onClick={closeNavbar}
                    aria-label="Close menu"
                  >
                    <i className="ri-close-line" aria-hidden="true" />
                  </button>
                  <div className="header-nav-scroll">
                    <button
                      type="button"
                      className={`header-nav-scroll__arrow header-nav-scroll__arrow--left${headerNavScrollUi.enabled && headerNavScrollUi.canLeft ? "" : " is-hidden"}`}
                      aria-label="Scroll left"
                      onClick={() => {
                        const el = headerNavScrollRef.current;
                        if (!el) return;
                        el.scrollBy({ left: -Math.max(220, el.clientWidth * 0.55), top: 0, behavior: "smooth" });
                      }}
                    >
                      <i className="ri-arrow-left-s-line" aria-hidden="true" />
                    </button>

                    <div className="header-nav-scroll__viewport" ref={headerNavScrollRef}>
                      <ul className="navbar-nav me-auto mb-2 mb-lg-0">
                        {/* <li className="nav-item">
                      <Link
                        className={`nav-link ${isActive("/") ? "active" : ""}`}
                        to="/"
                        onClick={closeNavbar}
                      >
                        Home
                      </Link>
                    </li> */}
                        {/* <li
                          className={`nav-item dropdown ${isAnySubmenuActive(location.pathname, BUY_CRYPTO_SUBMENU_ITEMS) || openDropdown === "buy_crypto" ? "active" : ""}`}
                          onMouseEnter={() => openDropdownHover("buy_crypto")}
                          onMouseLeave={closeDropdownHover}
                        >
                          <span
                            className={`nav-link dropdown-toggle ${isAnySubmenuActive(location.pathname, BUY_CRYPTO_SUBMENU_ITEMS) || openDropdown === "buy_crypto" ? "active" : ""}`}
                            role="button"
                            style={{ cursor: "pointer" }}
                            onClick={() => toggleDropdown("buy_crypto")}
                          >
                            Buy Crypto
                          </span>
                          <ul className={`dropdown-menu ${openDropdown === "buy_crypto" ? "show" : ""}`} onMouseEnter={() => openDropdownHover("buy_crypto")} onMouseLeave={closeDropdownHover}>
                            {BUY_CRYPTO_SUBMENU_ITEMS.map((item) => (
                              <li key={item.id} className={item.className || (item.id ? `${item.id}-item` : "")}>
                                <Link className="dropdown-item" to={item.to} onClick={closeNavbar}>
                                  <img src={item.iconSrc} alt="" />
                                  <span className="dropdown-item-content">
                                    <span className="dropdown-item-text">
                                      {item.title}
                                      {item.titleSub ? <sub>{item.titleSub}</sub> : null}
                                    </span>
                                    <span className="dropdown-item-desc">{item.desc}</span>
                                  </span>
                                </Link>
                              </li>
                            ))}
                          </ul>
                        </li> */}
                        <li className="nav-item">
                          <Link
                            className={`nav-link ${isActive("/") && !location.hash ? "active" : ""}`}
                            to="/"
                            onClick={(e) => {
                              closeNavbar();
                              navigate("/");
                              window.scrollTo({ top: 0, behavior: "smooth" });
                            }}
                          >
                            Home
                          </Link>
                        </li>
                        <li className="nav-item">
                          <Link
                            className={`nav-link ${isActive("/") && location.hash === "#features" ? "active" : ""}`}
                            to="/#features"
                            onClick={(e) => handleNavClick(e, "features")}
                          >
                            Features
                          </Link>
                        </li>
                        <li className="nav-item">
                          <Link
                            className={`nav-link ${isActive("/") && location.hash === "#why-arab" ? "active" : ""}`}
                            to="/#why-arab"
                            onClick={(e) => handleNavClick(e, "why-arab")}
                          >
                            Why Arab Global
                          </Link>
                        </li>
                        {/* About Us Dropdown */}
                        <li
                          className={`nav-item dropdown ${isAboutMenuActiveRoute || openDropdown === "about" ? "active" : ""}`}
                          onMouseEnter={() => openDropdownHover("about")}
                          onMouseLeave={closeDropdownHover}
                        >
                          <span
                            className={`nav-link dropdown-toggle ${isAboutMenuActiveRoute || openDropdown === "about" ? "active" : ""}`}
                            role="button"
                            style={{ cursor: "pointer" }}
                            onClick={() => toggleDropdown("about")}
                          >
                            About Us
                          </span>
                          <ul
                            className={`dropdown-menu dropdown-menu--about ${openDropdown === "about" ? "show" : ""}`}
                            onMouseEnter={() => openDropdownHover("about")}
                            onMouseLeave={closeDropdownHover}
                          >
                            {ABOUT_SUBMENU_ITEMS.map((item) => (
                              <li key={item.id} className={item.className || (item.id ? `${item.id}-item` : "")}>
                                <Link className="dropdown-item" to={item.to} onClick={closeNavbar}>
                                  <img className="nav_submenu_icon dark_img" src={item.iconSrcDark} alt="" width={44} height={44} loading="lazy" />
                                  <img className="nav_submenu_icon light_img" src={item.iconSrcLight} alt="" width={44} height={44} loading="lazy" />
                                  <span className="dropdown-item-content">
                                    <span className="dropdown-item-text">{item.title}</span>
                                    <span className="dropdown-item-desc">{item.desc}</span>
                                  </span>
                                </Link>
                              </li>
                            ))}
                          </ul>
                        </li>
                        <li className="nav-item">
                          <Link
                            className={`nav-link ${isActive("/") && location.hash === "#support" ? "active" : ""}`}
                            to="/#support"
                            onClick={(e) => handleNavClick(e, "support")}
                          >
                            Support
                          </Link>
                        </li>

                        <li className="nav-item">
                          <Link
                            className={`nav-link ${isActive("/market") ? "active" : ""}`}
                            to="/market"
                            onClick={closeNavbar}
                          >
                            Market
                          </Link>
                        </li>

                        {/* Trade Dropdown */}
                        <li
                          className={`nav-item dropdown ${isAnySubmenuActive(location.pathname, TRADE_SUBMENU_ITEMS) || openDropdown === "trade" ? "active" : ""}`}
                          onMouseEnter={() => openDropdownHover("trade")}
                          onMouseLeave={closeDropdownHover}
                        >
                          <span
                            className={`nav-link dropdown-toggle ${isAnySubmenuActive(location.pathname, TRADE_SUBMENU_ITEMS) || openDropdown === "trade" ? "active" : ""}`}
                            role="button"
                            style={{ cursor: "pointer" }}
                            onClick={() => toggleDropdown("trade")}
                          >
                            Trade
                          </span>
                          <ul
                            className={`dropdown-menu dropdown-menu--trade ${openDropdown === "trade" ? "show" : ""}`}
                            onMouseEnter={() => openDropdownHover("trade")}
                            onMouseLeave={closeDropdownHover}
                          >
                            {TRADE_SUBMENU_ITEMS.map((item) => (
                              <li key={item.id} className={item.className || (item.id ? `${item.id}-item` : "")}>
                                <Link className="dropdown-item" to={item.to} onClick={closeNavbar}>
                                  <img className="nav_submenu_icon dark_img" src={item.iconSrcDark} alt="" width={44} height={44} loading="lazy" />
                                  <img className="nav_submenu_icon light_img" src={item.iconSrcLight} alt="" width={44} height={44} loading="lazy" />
                                  <span className="dropdown-item-content">
                                    <span className="dropdown-item-text">{item.title}</span>
                                    <span className="dropdown-item-desc">{item.desc}</span>
                                  </span>
                                </Link>
                              </li>
                            ))}
                          </ul>
                        </li>

                        {/* Futures Dropdown */}
                        <li
                          className={`nav-item dropdown ${isFuturesNavActive || openDropdown === "futures" ? "active" : ""}`}
                          onMouseEnter={() => openDropdownHover("futures")}
                          onMouseLeave={closeDropdownHover}
                        >
                          <span
                            className={`nav-link dropdown-toggle ${isFuturesNavActive || openDropdown === "futures" ? "active" : ""}`}
                            role="button"
                            style={{ cursor: "pointer" }}
                            onClick={() => toggleDropdown("futures")}
                          >
                            Futures
                          </span>
                          <ul
                            className={`dropdown-menu dropdown-menu--futures ${openDropdown === "futures" ? "show" : ""}`}
                            onMouseEnter={() => openDropdownHover("futures")}
                            onMouseLeave={closeDropdownHover}
                          >
                            {FUTURES_SUBMENU_ITEMS.map((item) => (
                              <li key={item.id} className={item.className || (item.id ? `${item.id}-item` : "")}>
                                <Link className="dropdown-item" to={item.to} onClick={closeNavbar}>
                                  <img className="nav_submenu_icon dark_img" src={item.iconSrcDark} alt="" width={44} height={44} loading="lazy" />
                                  <img className="nav_submenu_icon light_img" src={item.iconSrcLight} alt="" width={44} height={44} loading="lazy" />
                                  <span className="dropdown-item-content">
                                    <span className="dropdown-item-text">{item.title}</span>
                                    <span className="dropdown-item-desc">{item.desc}</span>
                                  </span>
                                </Link>
                              </li>
                            ))}
                          </ul>
                        </li>

                        {/* Earning Dropdown */}
                        <li
                          className={`nav-item dropdown ${isAnySubmenuActive(location.pathname, EARNING_SUBMENU_ITEMS) ||
                            openDropdown === "earning"
                            ? "active"
                            : ""
                            }`}
                          onMouseEnter={() => openDropdownHover("earning")}
                          onMouseLeave={closeDropdownHover}
                        >
                          <span
                            className={`nav-link dropdown-toggle ${isAnySubmenuActive(location.pathname, EARNING_SUBMENU_ITEMS) ||
                              openDropdown === "earning"
                              ? "active"
                              : ""
                              }`}
                            role="button"
                            style={{ cursor: "pointer" }}
                            onClick={() => toggleDropdown("earning")}
                          >
                            Earning
                          </span>
                          <ul
                            className={`dropdown-menu dropdown-menu--earning ${openDropdown === "earning" ? "show" : ""}`}
                            onMouseEnter={() => openDropdownHover("earning")}
                            onMouseLeave={closeDropdownHover}
                          >
                            {EARNING_SUBMENU_ITEMS.map((item) => (
                              <li key={item.id} className={item.className || (item.id ? `${item.id}-item` : "")}>
                                <Link className="dropdown-item" to={item.to} onClick={closeNavbar}>
                                  <img className="nav_submenu_icon dark_img" src={item.iconSrcDark} alt="" width={44} height={44} loading="lazy" />
                                  <img className="nav_submenu_icon light_img" src={item.iconSrcLight} alt="" width={44} height={44} loading="lazy" />
                                  <span className="dropdown-item-content">
                                    <span className="dropdown-item-text">{item.title}</span>
                                    <span className="dropdown-item-desc">{item.desc}</span>
                                  </span>
                                </Link>
                              </li>
                            ))}
                          </ul>
                        </li>

                        {/* <li className={`nav-item vip-otc-nav ${isOtcExecutionNavActive ? "active" : ""}`}>
                          <Link
                            className={`nav-link ${isOtcExecutionNavActive ? "active" : ""}`}
                            to={OTC_FORM_PATH}
                            onClick={closeNavbar}
                          >
                            {OTC_NAV_LABEL}
                          </Link>
                        </li> */}

                        {/* More — announcements, referral, help, blogs */}
                        <li
                          className={`nav-item dropdown ${isMoreMenuActiveRoute || openDropdown === "more" ? "active" : ""
                            }`}
                          onMouseEnter={() => openDropdownHover("more")}
                          onMouseLeave={closeDropdownHover}
                        >
                          <span
                            className={`nav-link dropdown-toggle ${isMoreMenuActiveRoute || openDropdown === "more" ? "active" : ""
                              }`}
                            role="button"
                            style={{ cursor: "pointer" }}
                            onClick={() => toggleDropdown("more")}
                          >
                            More
                          </span>
                          <ul
                            className={`dropdown-menu dropdown-menu--more ${openDropdown === "more" ? "show" : ""}`}
                            onMouseEnter={() => openDropdownHover("more")}
                            onMouseLeave={closeDropdownHover}
                          >
                            {MORE_SUBMENU_ITEMS.map((item) => (
                              <li key={item.id} className={item.className || (item.id ? `${item.id}-item` : "")}>
                                <Link className="dropdown-item" to={item.to} onClick={closeNavbar}>
                                  <img className="nav_submenu_icon dark_img" src={item.iconSrcDark} alt="" width={44} height={44} loading="lazy" />
                                  <img className="nav_submenu_icon light_img" src={item.iconSrcLight} alt="" width={44} height={44} loading="lazy" />
                                  <span className="dropdown-item-content">
                                    <span className="dropdown-item-text">
                                      {item.title}
                                      {item.titleSub ? <sub className="nav-title-badge--new">{item.titleSub}</sub> : null}
                                    </span>
                                    <span className="dropdown-item-desc">{item.desc}</span>
                                  </span>
                                </Link>
                              </li>
                            ))}
                          </ul>
                        </li>

                        {/* <li className="nav-item">
                          <Link
                            className={`nav-link ${isActive("/user_profile/square") ? "active" : ""}`}
                            to="/user_profile/square"
                            onClick={closeNavbar}
                          >
                            Square
                          </Link>
                        </li>
                        <li className="nav-item">
                          <Link
                            className={`nav-link ${isActive("/rewards") ? "active" : ""}`}
                            to="/rewards"
                            onClick={closeNavbar}
                          >
                            Rewards
                          </Link>
                        </li> */}

                        {/* <li className="nav-item mememenu">
                      <Link
                        className={`nav-link ${isActive("/meme") ? "active" : ""}`}
                        to="/meme"
                        onClick={closeNavbar}
                      >
                        Meme+
                      </Link>
                    </li> */}

                        {/* <li className="nav-item">
                      <Link
                        className={`nav-link ${isActive("/blogs") ? "active" : ""}`}
                        to="/blogs"
                        onClick={closeNavbar}
                      >
                        Blogs & News
                      </Link>
                    </li> */}
                        {/* <li className={`nav-item dropdown mbl ${isActive("/earning") || isActive("/referral") || openDropdown === "download" ? "active" : ""}`}>
                          <span
                            className={`nav-link dropdown-toggle ${isActive("/earning") || isActive("/referral") || openDropdown === "download" ? "active" : ""}`}
                            role="button"
                            style={{ cursor: "pointer" }}
                            onClick={() => toggleDropdown("download")}
                          >
                            <img src="/images/download_icon2.svg" alt="scan" width={12} /> Download
                          </span>
                          <ul className={`dropdown-menu ${openDropdown === "download" ? "show" : ""}`}>
                            <li>
                              <div className='qrcode'>
                                <div className="scan_img"><img src="/images/scan.png" alt="scan" /></div>
                                <p>Scan to Download App iOS & Android</p>
                                <button className='btn'>Download</button>
                              </div>
                            </li>
                          </ul>
                        </li> */}
                        {/* <li className="nav-item mbl">
                      <Link
                        className="nav-link"
                        to="/#"
                        onClick={(e) => {
                          e.preventDefault();
                          setThemeUpdated((prev) => !prev);
                          closeNavbar();
                        }}
                        role="button"
                        aria-label="Toggle theme"
                      >
                        Theme <span><img src="/images/themeicon.svg" alt="theme" /></span>
                      </Link>
                    </li> */}
                        <li className="nav-item mbl nsvqrcode">
                          <Link
                            className="nav-link"
                            to="/#"
                            onClick={(e) => {
                              e.preventDefault();
                              handleTheme();
                              setThemeUpdated((prev) => !prev);
                              closeNavbar();
                            }}
                            role="button"
                            aria-label="Toggle theme"
                          >
                            Theme <span>
                              <img
                                src={newStoredTheme === "dark" ? "/images/themeicon.svg" : "/images/themeicon.svg"}
                                alt="theme"
                              />
                            </span>
                          </Link>
                        </li>
                      </ul>
                    </div>

                    <button
                      type="button"
                      className={`header-nav-scroll__arrow header-nav-scroll__arrow--right${headerNavScrollUi.enabled && headerNavScrollUi.canRight ? "" : " is-hidden"}`}
                      aria-label="Scroll right"
                      onClick={() => {
                        const el = headerNavScrollRef.current;
                        if (!el) return;
                        el.scrollBy({ left: Math.max(220, el.clientWidth * 0.55), top: 0, behavior: "smooth" });
                      }}
                    >
                      <i className="ri-arrow-right-s-line" aria-hidden="true" />
                    </button>
                  </div>
                </div>
              </nav>
            </div>
          </div>

          <div className="col-lg-4">
            <div className="header_right">

              <div className="button_outer">
                <a className="search_icon" href="#" data-bs-toggle="modal" data-bs-target="#exampleModal">
                  <i className="ri-search-line"></i>
                </a>
                <button className="login_btn sign_btn" onClick={loginPage}>
                  <Link to="/login">Log In</Link>
                </button>
                <button className="login_btn" onClick={signupPage}>
                  <Link to="/signup">Sign Up</Link>
                </button>
                <div
                  className="themecolor_icon cursor-pointer"
                  onClick={() => {
                    handleTheme();
                    setThemeUpdated((prev) => !prev);
                  }}
                  title="Toggle Theme"
                >
                  <i
                    className={newStoredTheme === "dark" ? "ri-sun-line dark-text" : "ri-moon-line light-text"}
                    aria-hidden="true"
                  />
                </div>


                {/* <div className="downloadtabs">
                  <img src="/images/download_icon2.svg" alt="download" />
                  <div className='scantophdr'>
                    <div className='qrcode'>
                      <div className="scan_img"><img src="/images/scan.png" alt="scan" /></div>
                      <p>Scan to Download App iOS & Android</p>
                      <button className='btn'>Download</button>
                    </div>
                  </div>
                </div> */}
                {/* <div className="themeicon"><img src="/images/themeicon.svg" alt="theme" /></div> */}
              </div>
            </div>
          </div>

        </div>
      </div>
    </header>
  );
};

export default UserHeader;

